import { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Typography,
  InputBase,
  Badge,
  Tooltip,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Outlet, useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import ChatIcon from '@mui/icons-material/Chat';
import LinkIcon from '@mui/icons-material/Link';
import { useGetAuthUserQuery } from 'redux/apiSlices/authUser.slice';
import { getFirstLetters, stringToColor } from 'utils/utils';
import { auth } from 'firebaseServices/firebase.config';
import { clearAllCachedData } from 'redux/store';
import { EShortcutTypes } from 'shared/types/generic.types';
import logo from 'assets/images/WF_logo.svg';
import PinnedContent from './PinnedContent/PinnedContent';
import DrawerContent from './DrawerContent/DrawerContent';

const PinedLayout = () => {
  const navigate = useNavigate();
  const { data } = useGetAuthUserQuery();
  const shortcuts = data?.settings?.shortcuts || [];

  const [openedShortcutId, setOpenedShortcutId] = useState<string>('');
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [anchorElShortcuts, setAnchorElShortcuts] = useState<null | HTMLElement>(null);

  const shortName = getFirstLetters(data?.name);
  const avatarColor = stringToColor(data?.name);

  const handleLogout = () => {
    auth.signOut();
    clearAllCachedData();
    setAnchorElUser(null);
  };

  const getShortcutIcon = (type: string) => {
    switch (type) {
      case EShortcutTypes.BOARD:
        return <DashboardIcon fontSize="small" />;
      case EShortcutTypes.CUSTOM_DOC:
        return <DescriptionIcon fontSize="small" />;
      case EShortcutTypes.CHAT:
        return <ChatIcon fontSize="small" />;
      default:
        return <LinkIcon fontSize="small" />;
    }
  };

  const handleShortcutClick = (shortcutId: string) => {
    setOpenedShortcutId(shortcutId);
    setAnchorElShortcuts(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Header */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: 56, px: { xs: 2, sm: 3 } }}>
          {/* Left - Logo */}
          <Box
            component="img"
            alt="Workfeed"
            src={logo}
            onClick={() => navigate('/projects')}
            sx={{
              height: 28,
              cursor: 'pointer',
              filter: 'brightness(0) saturate(100%) invert(22%) sepia(89%) saturate(1832%) hue-rotate(215deg) brightness(91%) contrast(98%)',
            }}
          />

          {/* Center - Search (desktop only) */}
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              backgroundColor: 'background.default',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              px: 2,
              py: 0.5,
              width: '100%',
              maxWidth: 400,
              mx: 3,
              transition: 'border-color 0.2s',
              '&:focus-within': {
                borderColor: 'primary.main',
              },
            }}
          >
            <SearchIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
            <InputBase
              placeholder="Search..."
              sx={{
                flex: 1,
                fontSize: '0.875rem',
              }}
            />
          </Box>

          {/* Right - Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Shortcuts Button */}
            {shortcuts.length > 0 && (
              <Tooltip title="Pinned items">
                <IconButton
                  onClick={(e) => setAnchorElShortcuts(e.currentTarget)}
                  sx={{ color: 'text.secondary' }}
                >
                  <Badge badgeContent={shortcuts.length} color="primary" max={9}>
                    <BookmarkIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}

            {/* User Avatar */}
            <IconButton onClick={(e) => setAnchorElUser(e.currentTarget)} sx={{ p: 0.5, ml: 1 }}>
              <Avatar
                alt={data?.name}
                sx={{
                  bgcolor: avatarColor,
                  width: 32,
                  height: 32,
                  fontSize: '0.8rem',
                  fontWeight: 600,
                }}
              >
                {shortName}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Shortcuts Menu */}
      <Menu
        anchorEl={anchorElShortcuts}
        open={Boolean(anchorElShortcuts)}
        onClose={() => setAnchorElShortcuts(null)}
        PaperProps={{
          sx: { width: 280, maxHeight: 400 },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Pinned Items
          </Typography>
        </Box>
        <Divider />
        <List sx={{ py: 0.5 }}>
          {shortcuts.map((shortcut) => (
            <ListItemButton
              key={shortcut.id}
              onClick={() => handleShortcutClick(shortcut.id)}
              sx={{ py: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {getShortcutIcon(shortcut.type)}
              </ListItemIcon>
              <ListItemText
                primary={shortcut.name}
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  noWrap: true,
                }}
              />
            </ListItemButton>
          ))}
        </List>
      </Menu>

      {/* User Menu */}
      <Menu
        anchorEl={anchorElUser}
        open={Boolean(anchorElUser)}
        onClose={() => setAnchorElUser(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: { minWidth: 200, mt: 1 },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="body2" fontWeight={600}>
            {data?.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
            {data?.email}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          Sign out
        </MenuItem>
      </Menu>

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Outlet />
        </Box>

        {/* Drawer for shortcut content */}
        <DrawerContent
          shortcuts={shortcuts}
          openedShortcutId={openedShortcutId}
          setOpenedShortcutId={setOpenedShortcutId}
          userId={data?.id || ''}
        />

        {/* Pinned content panel */}
        <PinnedContent shortcuts={shortcuts} userId={data?.id || ''} />
      </Box>
    </Box>
  );
};

export default PinedLayout;
